/**
 * graze.social/gym — the Feed Gym's research, in public, with no login.
 *
 * The gym spends real readers' attention. What it learns should therefore be readable by the
 * people whose attention paid for it, by the creators deciding whether to lend a slice of their
 * page, and by anyone who wants to argue with the method.
 *
 * 🔴 WHAT IS NOT ON THIS PAGE, and why:
 *
 *   - Which feed contributed what. A finding is a claim about a population; naming contributors
 *     would publish an audience's behaviour to anyone who asked. Feed COUNTS, yes. Names, no.
 *   - Internal identifiers. An experiment is a name and a result.
 *   - A green tick on an instrument that has not been certified. The check has THREE states, and
 *     "not yet" is not "fine" — the previous version computed `passed = verdict !== "open"`, which
 *     is true of a failure, and would have shown a tick at the moment the instrument was shown
 *     unsound.
 *
 * 🔴 WHAT IS ON IT AND MIGHT SURPRISE: the reward weights. An experiment whose scoring is private
 * is not a public result, and the weights are the single thing a sceptical reader most needs in
 * order to disagree with a verdict.
 */
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { createHead } from "@/components/createHead";
import { gymPublicQueryOptions, type GymExperimentView } from "@/lib/api";

export const Route = createFileRoute("/_marketing-header-layout/gym")({
  component: GymPublicBody,
  /**
   * 🔴 THE LOADER IS WHAT MAKES THE QUERY RUN AT ALL, and that is not obvious.
   *
   * This router uses `setupRouterSsrQueryIntegration`: a route's data is fetched in its loader,
   * dehydrated into the document, and rehydrated on the client. A `useQuery` with no loader behind
   * it renders its pending state server-side and then sits there — which is exactly what this page
   * did on its first deploy, showing "Loading the gym's results…" for ever with no error, no
   * console message and no request ever leaving the browser.
   *
   * 🔴 IT MUST NOT THROW. A public page that 500s because the API is briefly unreachable is worse
   * than one that says so; swallowing here lets the component's own error state do the talking,
   * and `useQuery` still retries on the client.
   */
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(gymPublicQueryOptions()).catch(() => null),
  head: ({ match: { pathname } }) =>
    createHead({
      title: "Feed Gym | Graze",
      description:
        "What Graze is learning about how feeds should be built, tested on real feeds with their creators' permission.",
      ogTitle: "Feed Gym",
      pathname,
    }),
});

/** A win rate as a sentence fragment, or a dash. Percentages, never decimals. */
function rate(value: number | null): string {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

function range(low: number | null, high: number | null): string | null {
  if (low === null || high === null || low === undefined || high === undefined) return null;
  return `${Math.round(low * 100)}–${Math.round(high * 100)}%`;
}

function when(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * The instrument's standing, in three states.
 *
 * Expressed here rather than server-side because the wording is a product decision and the counts
 * are a fact. "Not yet certified" is reported as what it is — not a failure, and not a pass.
 */
function because(reason: string): string {
  const trimmed = reason.replace(/^\s*chosen because\s+/i, "")
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1)
}

function instrumentLine(check: { sentence: string | null } | null): string {
  // 🔴 THE GYM'S OWN SENTENCE, NOT A REBUILD OF IT FROM A PERCENTAGE.
  //
  // This used to read `pct >= 45 && pct <= 55` and call that "even" — a third implementation of a
  // judgement that `Instrument.state` already makes, and the crudest of the three. The check has
  // THREE states and the third is not a failure: 9 of 18 is fifty per cent and NOT certified, and
  // a band on the rate cannot tell those apart because the distinction is about how much evidence
  // there is, not where it sits. `AA_MIN_DECISIVE` never appeared in the band at all.
  //
  // So the wording comes from the thing that knows. If it is ever absent, say that rather than
  // guess — a sentence invented here is exactly what went wrong.
  return check?.sentence ?? "The instrument check cannot be read right now.";
}

/* ---------------------------------------------------------------- visual pieces */

/**
 * 🔴 EVERY RAW SCALE STEP NEEDS ITS DARK TWIN.
 *
 * `bg-slate-3` is a near-white; in dark mode this app does NOT remap it, it expects
 * `dark:bg-slatedark-3` alongside (see the `@layer base` rule in styles.css, and every other
 * component in this tree). The first cut of this page used bare steps throughout, which drew
 * light-mode chrome on a dark ground. Semantic utilities — `text-slate-dim`, `bg-amber-subtle`,
 * `border-green-dim` — already carry both themes and are preferred wherever one fits.
 */
const TONES = {
  finding: {
    bar: "bg-green-9 dark:bg-greendark-9",
    span: "bg-green-7 dark:bg-greendark-7",
    rail: "bg-green-9 dark:bg-greendark-9",
    chip: "border-green-dim bg-green-subtle text-green-normal",
  },
  running: {
    bar: "bg-amber-9 dark:bg-amberdark-9",
    span: "bg-amber-7 dark:bg-amberdark-7",
    rail: "bg-amber-9 dark:bg-amberdark-9",
    chip: "border-amber-dim bg-amber-subtle text-amber-normal",
  },
  settled: {
    bar: "bg-slate-9 dark:bg-slatedark-9",
    span: "bg-slate-7 dark:bg-slatedark-7",
    rail: "bg-slate-7 dark:bg-slatedark-7",
    chip: "border-slate-dim bg-slate-subtle text-slate-dim",
  },
} as const;

type Tone = keyof typeof TONES;

function toneOf(status: string): Tone {
  if (status === "positive" || status === "negative") return "finding";
  if (status === "running" || status === "pending") return "running";
  return "settled";
}

/**
 * A result as a picture: where the layer's win rate landed, against the even line it has to beat.
 *
 * 🔴 THE EVEN LINE IS DRAWN, NOT IMPLIED. The whole claim a result makes is "further from half
 * than chance explains", and a bare percentage asks the reader to hold that comparison in their
 * head. A reader who does not know where half is cannot tell 52 from 61, and those are the
 * difference between nothing and a finding.
 *
 * So the scale is two-sided by construction: the track's left half belongs to the feed's own
 * ordering and its right half to the layer under test, and the drawn bar runs FROM THE CENTRE to
 * the observed rate. Its length is the size of the claim and its direction is who won — neither
 * of which a bar filled from zero can say.
 *
 * The credible interval is the pale span behind it, and it is the honest part: a rate with a wide
 * interval has not settled, and the picture should say so before the number does.
 */
function Scale({
  rate,
  low,
  high,
  tone,
}: {
  rate: number;
  low: number | null;
  high: number | null;
  tone: Tone;
}) {
  const t = TONES[tone];
  const clamp = (value: number) => Math.max(0, Math.min(1, value));
  const observed = clamp(rate);
  const lo = low === null || low === undefined ? null : clamp(low);
  const hi = high === null || high === undefined ? null : clamp(high);
  const from = Math.min(observed, 0.5);
  const reach = Math.abs(observed - 0.5);
  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-11 overflow-hidden rounded-md ring-1 ring-inset ring-slate-6 dark:ring-slatedark-6">
        {/* the two sides of the question, so direction reads before any number does */}
        <div className="absolute inset-y-0 left-0 w-1/2 bg-slate-3 dark:bg-slatedark-3" />
        <div className="absolute inset-y-0 right-0 w-1/2 bg-teal-3 dark:bg-tealdark-3" />
        {/* quarter marks: a scale a reader can measure against without a legend */}
        {[0.25, 0.75].map((tick) => (
          <div
            key={tick}
            className="absolute bottom-0 h-1.5 w-px bg-slate-7 dark:bg-slatedark-7"
            style={{ left: `${tick * 100}%` }}
          />
        ))}
        {lo !== null && hi !== null ? (
          <div
            className={`absolute inset-y-2 rounded-sm opacity-40 ${t.span}`}
            style={{ left: `${lo * 100}%`, width: `${(hi - lo) * 100}%` }}
          />
        ) : null}
        <div
          className={`absolute inset-y-[15px] ${t.bar}`}
          style={{ left: `${from * 100}%`, width: `${reach * 100}%` }}
        />
        <div
          className={`absolute inset-y-0 -ml-px w-0.5 ${t.bar}`}
          style={{ left: `${observed * 100}%` }}
        />
        <div className="absolute inset-y-0 left-1/2 w-px bg-slate-9 dark:bg-slatedark-9" />
      </div>
      {/* The swatches tie each label to its half of the track, which is cheaper to read than
          remembering which end is which on the next result down the page.

          🔴 FLOW, NOT ABSOLUTE POSITIONS. These were pinned at left-0 / left-1/2 / right-0, which
          collides below about 560px: the centre label ran straight into "the feed's own ordering"
          and rendered as "...own orderingEVEN the layer...". That is every phone. Found by
          framing the page at 540px for the demo cut, which is the width a narrow layout actually
          gets — the desktop view never shows it.

          The centre label does not need to be pixel-centred, because the even LINE is drawn on the
          track above it; this word is a legend for that line, not the mark itself. */}
      <div className="flex items-center justify-between gap-2 text-[10px] text-slate-dim">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="size-2 shrink-0 rounded-[1px] bg-slate-5 dark:bg-slatedark-5" />
          the feed&rsquo;s own ordering
        </span>
        <span className="shrink-0 tracking-[0.12em] uppercase">even</span>
        <span className="flex min-w-0 items-center gap-1.5 text-right">
          the layer being tested
          <span className="size-2 shrink-0 rounded-[1px] bg-teal-5 dark:bg-tealdark-5" />
        </span>
      </div>
    </div>
  );
}

/** The measured rate, set as a figure rather than as running text. */
function Figure({
  value,
  note,
  size = "text-4xl",
}: {
  value: string;
  note: string | null;
  size?: string;
}) {
  return (
    <div className="flex shrink-0 items-baseline gap-2 sm:w-32 sm:flex-col sm:items-start sm:gap-0.5">
      <span className={`instrument-serif ${size} leading-none text-slate-normal tabular-nums`}>
        {value}
      </span>
      {note ? <span className="text-xs text-slate-dim tabular-nums">{note}</span> : null}
    </div>
  );
}

/** A number said as a number, because most of this page's facts are quantities. */
function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="instrument-serif text-3xl leading-none text-slate-normal tabular-nums md:text-4xl">
        {value}
      </span>
      <span className="text-xs text-slate-dim">{label}</span>
    </div>
  );
}

/** What happened, as a shape as well as a word — so the list is scannable without reading it. */
function Outcome({ experiment }: { experiment: GymExperimentView }) {
  const tone = toneOf(experiment.status);
  const live = tone === "running";
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs ${TONES[tone].chip}`}
    >
      {live ? (
        <span className="size-1.5 animate-pulse rounded-full bg-current motion-reduce:animate-none" />
      ) : null}
      {experiment.outcome}
    </span>
  );
}

/** A heading with a rule, so a long page reads as sections rather than as one column of text. */
function SectionHead({ title, note }: { title: string; note?: string | null }) {
  return (
    <div className="flex items-baseline gap-4">
      <h2 className="instrument-serif shrink-0 text-3xl tracking-tight text-slate-normal md:text-4xl">
        {title}
      </h2>
      <span className="grow border-b border-slate-dim" />
      {note ? <span className="shrink-0 text-xs text-slate-dim">{note}</span> : null}
    </div>
  );
}

function Experiment({ experiment }: { experiment: GymExperimentView }) {
  const spread = range(experiment.low, experiment.high);
  const tone = toneOf(experiment.status);
  return (
    <article className="relative border-b border-slate-dim py-6 pl-5 last:border-b-0">
      <div className={`absolute top-6 bottom-6 left-0 w-[3px] rounded-full ${TONES[tone].rail}`} />
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="instrument-serif text-xl text-slate-normal">{experiment.name}</h3>
          <Outcome experiment={experiment} />
        </div>
        {experiment.winRate !== null && experiment.winRate !== undefined ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
            <Figure value={rate(experiment.winRate)} note={spread} />
            <div className="min-w-0 grow">
              <Scale
                rate={experiment.winRate}
                low={experiment.low}
                high={experiment.high}
                tone={tone}
              />
            </div>
          </div>
        ) : null}
        {experiment.summary ? (
          <p className="max-w-prose text-sm text-slate-dim">{experiment.summary}</p>
        ) : null}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-dim tabular-nums">
          {experiment.decisive ? <span>{experiment.decisive} decisive visits</span> : null}
          {experiment.feeds ? <span>{experiment.feeds} feeds</span> : null}
          {experiment.finished ? <span>{when(experiment.finished)}</span> : null}
        </div>
      </div>
    </article>
  );
}

/**
 * The page itself, exported so a Storybook story can mount it.
 *
 * 🔴 EXPORTED SO THE DEMO CUTS SHOW THE REAL PAGE. `demos/GymCut.stories.tsx` used to import a
 * `GymPageBody` that only ever existed on an unmerged branch, so the gym cut could not be rebuilt
 * from main at all — and its findings beats were authored illustrations because of it. A component
 * a story cannot mount is a component whose marketing material drifts from the product.
 *
 * It takes no props on purpose: it reads `gymPublicQueryOptions` like the route does, so a story
 * seeds that query with a real production payload and gets the real page rather than a replica.
 */
export function GymPublicBody() {
  const { data, isPending, isError } = useQuery(gymPublicQueryOptions());

  if (isPending) {
    return <Shell>Loading the gym's results…</Shell>;
  }
  // A public page that 500s is worse than one that says it cannot reach the results.
  if (isError || !data) {
    return <Shell>The gym's results cannot be loaded right now. Please try again shortly.</Shell>;
  }

  // Derived here rather than served, because they are presentation: "answered" means a question
  // that reached a verdict, which is what a reader means by it, and is not a status the API owns.
  const settled = data.experiments.filter(
    (e) => e.status !== "running" && e.status !== "pending",
  ).length;
  const visits =
    data.experiments.reduce((total, e) => total + (e.decisive ?? 0), 0) + (data.now?.decisive ?? 0);
  // What certification needs, as the gym defines it — AA_MIN_DECISIVE, not a number repeated here.
  const minimum = data.instrument?.minimum ?? 200;

  return (
    <Shell>
      <section className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <span className="text-[11px] tracking-[0.18em] text-slate-dim uppercase">
            Graze research, in public
          </span>
          <h1 className="instrument-serif text-5xl tracking-tight text-balance text-slate-normal md:text-6xl">
            The Feed Gym
          </h1>
          <p className="max-w-prose text-lg text-slate-dim">
            Graze tests one idea at a time about how a feed should be built, on real feeds whose
            creators lend a slice of every page to the experiment. Each test runs until readers have
            told us something, and every result — including the ones that found nothing — is here.
          </p>
        </div>
        {/* The scale of the thing, before any of its conclusions. A reader deciding whether to
            believe this page wants to know how much was actually measured. */}
        <div className="grid grid-cols-3 divide-x divide-slate-6 dark:divide-slatedark-6">
          <div className="pr-4">
            <Stat
              value={data.enrolledFeeds}
              label={`feed${data.enrolledFeeds === 1 ? "" : "s"} taking part`}
            />
          </div>
          <div className="px-4">
            <Stat value={settled} label={`question${settled === 1 ? "" : "s"} answered`} />
          </div>
          <div className="px-4">
            <Stat value={visits.toLocaleString()} label="decisive visits gathered" />
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <SectionHead title="Testing now" />
        {data.now ? (
          <div className="flex flex-col gap-4 rounded-xl border border-amber-dim bg-amber-subtle p-5 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="instrument-serif text-2xl text-slate-normal">{data.now.name}</h3>
              <Outcome experiment={data.now} />
            </div>
            {data.now.winRate !== null && data.now.winRate !== undefined ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                <Figure value={rate(data.now.winRate)} note="so far" size="text-5xl" />
                <div className="min-w-0 grow">
                  <Scale
                    rate={data.now.winRate}
                    low={data.now.low}
                    high={data.now.high}
                    tone="running"
                  />
                </div>
              </div>
            ) : (
              /* 🔴 THIS USED TO SAY "no visit has been decisive yet" DIRECTLY ABOVE "382
                 decisive visits". `winRate` is served only from the frozen `result`, which is
                 correct — a live rate on a running test is a peeking surface, and publishing one
                 invites exactly the reading the gym exists to avoid. But the UI keyed "nothing
                 has happened" off that null, when the null means "not settled". */
              <p className="text-sm text-slate-dim">
                Gathering. The rate is published when the test settles, not before — a running
                total invites calling it early, which is the mistake this whole method is built
                to avoid.
              </p>
            )}
            {data.now.summary ? (
              <p className="max-w-prose text-sm text-slate-dim">{data.now.summary}</p>
            ) : null}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-dim tabular-nums">
              <span>started {when(data.now.started)}</span>
              <span>{data.now.decisive ?? 0} decisive visits</span>
              <span>{data.now.feeds ?? 0} feeds</span>
            </div>
            {data.now.reason ? (
              <p className="max-w-prose border-t border-amber-dim pt-3 text-xs text-slate-dim">
                Chosen because {because(data.now.reason)}
              </p>
            ) : null}
          </div>
        ) : data.next ? (
          <p className="text-slate-dim">
            <span className="text-slate-normal">{data.next.name}</span> is about to start.
          </p>
        ) : (
          <p className="text-slate-dim">
            Nothing is being tested at the moment. The next question opens as soon as the last one is
            settled.
          </p>
        )}
      </section>

      {data.proven.length > 0 ? (
        <section className="flex flex-col gap-4">
          <SectionHead
            title="What works"
            note={`${data.proven.length} layer${data.proven.length === 1 ? "" : "s"}`}
          />
          <p className="max-w-prose text-slate-dim">
            Layers readers measurably preferred. If you run a feed on Graze you can add any of these
            to it from your feed&rsquo;s Gym page.
          </p>
          <div className="flex flex-col">
            {data.proven.map((experiment) => (
              <Experiment key={experiment.id} experiment={experiment} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-4">
        <SectionHead
          title="Everything we have tested"
          note={
            data.experiments.length > 0
              ? `${data.experiments.length} question${data.experiments.length === 1 ? "" : "s"}`
              : null
          }
        />
        {data.experiments.length === 0 ? (
          <p className="text-slate-dim">Nothing has finished yet.</p>
        ) : (
          <div className="flex flex-col">
            {data.experiments.map((experiment) => (
              <Experiment key={experiment.id} experiment={experiment} />
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <SectionHead title="How this works" />
        <div className="flex max-w-prose flex-col gap-3 text-slate-dim">
          <p>
            A creator lends a share of every page — a quarter by default — to the gym. In those
            slots, readers see the layer being tested alongside what the feed would otherwise have
            shown, mixed together so neither gets a better position than the other.
          </p>
          <p>
            A <span className="text-slate-normal">visit</span> is one reader, one feed, one sitting.
            A visit is <span className="text-slate-normal">decisive</span> when the reader engaged
            with one side more than the other; most visits are not, because most people read without
            acting, and those count towards nothing.
          </p>
          <p>
            A test ends when readers have preferred one side clearly, when any difference is too
            small to matter, or after three weeks — in which case the honest answer is that the
            difference is smaller than this much traffic can show, and we say so.
          </p>
          <p>Nothing changes a feed automatically. A creator adds a proven layer if they want it.</p>
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <SectionHead title="The instrument" />
        <p className="max-w-prose text-slate-dim">
          On one page in ten, both halves are the same ranking. If our method were favouring a
          position rather than a design, that check would drift away from half and every result here
          would be suspect. So we watch it, in public, whether or not it is flattering.
        </p>
        {data.instrument && data.instrument.decisive > 0 ? (
          <div className="flex flex-col gap-5 rounded-xl border border-slate-dim bg-slate-subtle p-5 md:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
              <Figure
                value={rate(data.instrument.wins / data.instrument.decisive)}
                note={
                  // The null is MEASURED, not a half. Saying "should be 50%" beside a check whose
                  // own null is 0.508 states the wrong target by half a point, on the one figure
                  // the page asks the reader to be suspicious about.
                  data.instrument.null !== null && data.instrument.null !== undefined
                    ? `should be ${rate(data.instrument.null)}`
                    : "should be even"
                }
              />
              <div className="min-w-0 grow">
                <Scale
                  rate={data.instrument.wins / data.instrument.decisive}
                  low={null}
                  high={null}
                  tone="settled"
                />
              </div>
            </div>
            {/* How far from being able to answer, as a bar rather than a fraction — "46 of 200"
                is a quantity most readers will not convert. */}
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between text-[10px] tracking-[0.12em] text-slate-dim uppercase">
                <span>progress towards certifying</span>
                <span className="tabular-nums">
                  {Math.min(data.instrument.decisive, minimum)} / {minimum} visits
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-4 dark:bg-slatedark-4">
                <div
                  className="h-full rounded-full bg-teal-9 dark:bg-tealdark-9"
                  style={{
                    width: `${Math.min(100, (data.instrument.decisive / minimum) * 100)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-slate-dim tabular-nums">
                {instrumentLine(data.instrument)}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-dim">{instrumentLine(data.instrument)}</p>
        )}
      </section>

      {data.weights ? (
        <section className="flex flex-col gap-4">
          <SectionHead title="What counts as engagement" />
          <p className="max-w-prose text-slate-dim">
            Rarer actions count for more, because a signal people give seldom says more than one
            they give constantly. These are measured from the last {data.weights.windowDays} days
            across all of Graze, not chosen.
          </p>
          {/* 🔴 SIGNED WEIGHTS NEED A SIGNED SCALE. Drawn as lengths from zero, "asking for less
              like this" at -1000 and "a quote post" at +1000 were the same bar, differing only in
              colour — a reader scanning shapes would have read the strongest negative signal on
              the list as the strongest positive one. The zero line is now down the middle and
              every bar grows away from it, so direction is the first thing the row says.
              Zero-weight events keep their row, because "counts for nothing" is worth seeing. */}
          <div className="flex flex-col">
            {data.weights.events.map((weight) => {
              const top = Math.max(...data.weights!.events.map((e) => Math.abs(e.weight)), 1);
              const reach = (Math.abs(weight.weight) / top) * 50;
              const negative = weight.weight < 0;
              return (
                <div
                  key={weight.event}
                  className="flex flex-col gap-1.5 border-b border-slate-dim py-3 last:border-b-0"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-slate-normal">{weight.label}</span>
                    <span className="shrink-0 text-xs text-slate-dim tabular-nums">
                      {negative ? "−" : ""}
                      {Math.abs(Math.round(weight.weight))}
                    </span>
                  </div>
                  <div className="relative h-2 overflow-hidden rounded-[2px] bg-slate-3 dark:bg-slatedark-3">
                    <div
                      className={`absolute inset-y-0 ${
                        negative ? "bg-red-9 dark:bg-reddark-9" : "bg-teal-9 dark:bg-tealdark-9"
                      }`}
                      style={{
                        left: negative ? `${50 - reach}%` : "50%",
                        width: `${reach}%`,
                      }}
                    />
                    <div className="absolute inset-y-0 left-1/2 w-px bg-slate-7 dark:bg-slatedark-7" />
                  </div>
                  {weight.note ? <span className="text-xs text-slate-dim">{weight.note}</span> : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-slate-normal w-screen md:w-auto md:max-w-3xl min-h-screen mx-auto pt-14 pb-20 flex flex-col gap-10 px-4 md:px-10 instrument-sans">
      {children}
    </div>
  );
}
